import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, UserCircle, Mail, Phone, Calendar, Archive, RotateCcw, Eye, EyeOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { motion } from "framer-motion";

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2
  }).format(amount);
};

const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    position: "",
    department: "",
    start_date: "",
    salary: "",
    insurance_no: "",
    notes: ""
  });

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/employees?include_inactive=${showInactive}`, { withCredentials: true });
      setEmployees(response.data);
    } catch (error) {
      toast.error("Personel listesi yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const handleArchive = async (id) => {
    if (!window.confirm("Bu personeli pasife almak (arşivlemek) istediğinizden emin misiniz?")) return;
    try {
      await axios.post(`${API}/employees/${id}/archive`, {}, { withCredentials: true });
      toast.success("Personel arşivlendi");
      fetchEmployees();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const handleRestore = async (id) => {
    try {
      await axios.post(`${API}/employees/${id}/restore`, {}, { withCredentials: true });
      toast.success("Personel aktife alındı");
      fetchEmployees();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        salary: parseFloat(formData.salary)
      };
      
      if (editingEmployee) {
        await axios.put(`${API}/employees/${editingEmployee.id}`, data, { withCredentials: true });
        toast.success("Personel güncellendi");
      } else {
        await axios.post(`${API}/employees`, data, { withCredentials: true });
        toast.success("Personel eklendi");
      }
      setDialogOpen(false);
      resetForm();
      fetchEmployees();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu personeli silmek istediğinizden emin misiniz?")) return;
    try {
      await axios.delete(`${API}/employees/${id}`, { withCredentials: true });
      toast.success("Personel silindi");
      fetchEmployees();
    } catch (error) {
      toast.error("Silme başarısız");
    }
  };

  const openEditDialog = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      name: employee.name || "",
      email: employee.email || "",
      phone: employee.phone || "",
      position: employee.position || "",
      department: employee.department || "",
      start_date: employee.start_date || "",
      salary: employee.salary?.toString() || "",
      insurance_no: employee.insurance_no || "",
      notes: employee.notes || ""
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingEmployee(null);
    setFormData({
      name: "",
      email: "",
      phone: "",
      position: "",
      department: "",
      start_date: "",
      salary: "",
      insurance_no: "",
      notes: ""
    });
  };

  const filteredEmployees = employees.filter(e => 
    e.name?.toLowerCase().includes(search.toLowerCase()) ||
    e.position?.toLowerCase().includes(search.toLowerCase()) ||
    e.department?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="employees-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-outfit font-semibold text-zinc-50">Personel</h1>
          <p className="text-zinc-500 text-sm mt-1">
            {employees.filter(e => e.is_active !== false).length} aktif, {employees.filter(e => e.is_active === false).length} pasif personel
          </p>
        </div>
        <Button 
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="bg-amber-400 hover:bg-amber-300 text-zinc-950"
          data-testid="add-employee-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Yeni Personel
        </Button>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input 
            placeholder="Personel ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-zinc-900 border-zinc-800"
            data-testid="employee-search"
          />
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-zinc-900 border border-zinc-800 rounded-sm">
          <Switch 
            checked={showInactive}
            onCheckedChange={setShowInactive}
            data-testid="show-inactive-toggle"
          />
          <Label className="text-zinc-400 text-sm flex items-center gap-2">
            {showInactive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            Pasif personeli göster
          </Label>
        </div>
      </div>

      {/* Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredEmployees.map((employee, index) => {
          const isActive = employee.is_active !== false;
          return (
          <motion.div
            key={employee.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            onClick={() => openEditDialog(employee)}
            className={`bg-zinc-900 border border-zinc-800 p-6 rounded-sm cursor-pointer hover:border-zinc-700 transition-colors ${!isActive ? 'opacity-60' : ''}`}
            data-testid={`employee-card-${employee.id}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isActive ? 'bg-zinc-800' : 'bg-rose-900/30'}`}>
                  <UserCircle className={`w-7 h-7 ${isActive ? 'text-zinc-500' : 'text-rose-400'}`} />
                </div>
                <div>
                  <h3 className="text-zinc-50 font-medium">{employee.name}</h3>
                  <p className="text-sm text-amber-400">{employee.position}</p>
                </div>
              </div>
              <div className="flex gap-1">
                <Pencil className="w-3 h-3 text-zinc-500 mt-2" />
                {isActive ? (
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={(e) => { e.stopPropagation(); handleArchive(employee.id); }}
                    className="hover:bg-amber-400/10 text-zinc-400 hover:text-amber-400 h-8 w-8"
                    title="Pasife Al"
                    data-testid={`archive-employee-${employee.id}`}
                  >
                    <Archive className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={(e) => { e.stopPropagation(); handleRestore(employee.id); }}
                    className="hover:bg-emerald-400/10 text-zinc-400 hover:text-emerald-400 h-8 w-8"
                    title="Aktife Al"
                    data-testid={`restore-employee-${employee.id}`}
                  >
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                )}
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={(e) => { e.stopPropagation(); handleDelete(employee.id); }}
                  className="hover:bg-rose-400/10 text-zinc-400 hover:text-rose-400 h-8 w-8"
                  data-testid={`delete-employee-${employee.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="mt-4 space-y-2 text-sm">
              {employee.department && (
                <p className="text-zinc-500">Departman: <span className="text-zinc-400">{employee.department}</span></p>
              )}
              {employee.email && (
                <p className="text-zinc-500 flex items-center gap-2">
                  <Mail className="w-3 h-3" /> <span className="text-zinc-400">{employee.email}</span>
                </p>
              )}
              {employee.phone && (
                <p className="text-zinc-500 flex items-center gap-2">
                  <Phone className="w-3 h-3" /> <span className="text-zinc-400">{employee.phone}</span>
                </p>
              )}
              <p className="text-zinc-500 flex items-center gap-2">
                <Calendar className="w-3 h-3" /> 
                <span className="text-zinc-400">Başlangıç: {employee.start_date}</span>
              </p>
              {employee.end_date && (
                <p className="text-rose-400/70 flex items-center gap-2 text-xs">
                  Ayrılış: {employee.end_date}
                </p>
              )}
            </div>

            {/* Status badge */}
            {!isActive && (
              <div className="mt-2 px-2 py-1 bg-rose-900/30 text-rose-400 text-xs rounded inline-block">
                Pasif
              </div>
            )}

            <div className="mt-4 pt-4 border-t border-zinc-800 flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500">Maaş</p>
                <p className="text-lg font-mono text-zinc-50">{formatCurrency(employee.salary)}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-zinc-500">İzin</p>
                <p className="text-sm text-zinc-400">
                  {employee.used_leaves || 0} / {employee.total_leaves || 14} gün
                </p>
              </div>
            </div>
          </motion.div>
        );
        })}
        {filteredEmployees.length === 0 && (
          <div className="col-span-full py-12 text-center text-zinc-500">
            {search ? "Sonuç bulunamadı" : "Henüz personel eklenmemiş"}
          </div>
        )}
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 font-outfit">
              {editingEmployee ? "Personel Düzenle" : "Yeni Personel"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-zinc-400">Ad Soyad *</Label>
                <Input 
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="employee-name-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Pozisyon *</Label>
                <Input 
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  required
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="employee-position-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Departman</Label>
                <Input 
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="employee-department-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">E-posta</Label>
                <Input 
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="employee-email-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Telefon</Label>
                <Input 
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="employee-phone-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">İşe Başlama Tarihi *</Label>
                <Input 
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  required
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="employee-startdate-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Maaş (₺) *</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={formData.salary}
                  onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                  required
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="employee-salary-input"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-zinc-400">SGK Sicil No</Label>
                <Input 
                  value={formData.insurance_no}
                  onChange={(e) => setFormData({ ...formData, insurance_no: e.target.value })}
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="employee-insurance-input"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-zinc-400">Notlar</Label>
                <Textarea 
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="bg-zinc-950 border-zinc-800"
                  rows={2}
                  data-testid="employee-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
                className="border-zinc-700 hover:bg-zinc-800"
              >
                İptal
              </Button>
              <Button 
                type="submit"
                className="bg-amber-400 hover:bg-amber-300 text-zinc-950"
                data-testid="save-employee-btn"
              >
                {editingEmployee ? "Güncelle" : "Kaydet"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Employees;
